import { useState, useEffect, useCallback } from "react";
import { useDesignMode } from "@/hooks/useDesignMode";
import { toast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  X,
  Type,
  Palette,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Maximize2,
  Sparkles,
  RotateCcw,
  CaseUpper,
  CaseLower,
  CaseSensitive,
} from "lucide-react";
import { FONT_OPTIONS, FONT_WEIGHT_OPTIONS, ensureFontLoaded } from "./fontOptions";

const PERSISTED_STYLE_PROPS = [
  "color",
  "background-color",
  "background-image",
  "font-size",
  "font-family",
  "font-weight",
  "font-style",
  "text-decoration-line",
  "text-align",
  "text-transform",
  "letter-spacing",
  "line-height",
  "opacity",
  "border-radius",
  "width",
  "height",
  "max-width",
  "max-height",
  "min-width",
  "min-height",
  "object-fit",
];

const toKebabCase = (prop: string) => prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

const parseFontFamily = (cs: string): string => {
  const first = (cs || "").split(",")[0]?.trim().replace(/^['"]|['"]$/g, "") || "";
  return FONT_OPTIONS.find((f) => f.family === first)?.family || "";
};

const DesignDomPanel = () => {
  const { isDesignMode, selectedDom, setSelectedDom, setHasUnsavedChanges, upsertPendingDomOverride } = useDesignMode();

  const [textColor, setTextColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("transparent");
  const [fontSize, setFontSize] = useState(16);
  const [fontFamily, setFontFamily] = useState("");
  const [fontWeight, setFontWeight] = useState("400");
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [strike, setStrike] = useState(false);
  const [textAlign, setTextAlign] = useState("left");
  const [textTransform, setTextTransform] = useState("none");
  const [letterSpacing, setLetterSpacing] = useState(0);
  const [lineHeight, setLineHeight] = useState(1.5);
  const [opacity, setOpacity] = useState(100);
  const [borderRadius, setBorderRadius] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  // Gradient
  const [gradientEnabled, setGradientEnabled] = useState(false);
  const [gradFrom, setGradFrom] = useState("#7B2FFF");
  const [gradTo, setGradTo] = useState("#FF4D9D");
  const [gradAngle, setGradAngle] = useState(135);
  // Size
  const [elWidth, setElWidth] = useState<number>(0);
  const [elHeight, setElHeight] = useState<number>(0);
  const [lockRatio, setLockRatio] = useState(true);
  const [aspect, setAspect] = useState(1);

  // Sync panel state from selected element
  useEffect(() => {
    if (!selectedDom) return;
    const el = selectedDom.element;
    const cs = window.getComputedStyle(el);
    setTextColor(rgbToHex(cs.color));
    setBgColor(cs.backgroundColor === "rgba(0, 0, 0, 0)" ? "transparent" : rgbToHex(cs.backgroundColor));
    setFontSize(parseInt(cs.fontSize) || 16);
    setFontFamily(parseFontFamily(cs.fontFamily));
    setFontWeight(String(parseInt(cs.fontWeight) || 400));
    setItalic((cs.fontStyle || "").includes("italic"));
    const decoLine = cs.textDecorationLine || cs.textDecoration || "";
    setUnderline(decoLine.includes("underline"));
    setStrike(decoLine.includes("line-through"));
    setTextAlign(cs.textAlign || "left");
    setTextTransform(cs.textTransform || "none");
    const ls = parseFloat(cs.letterSpacing);
    setLetterSpacing(Number.isFinite(ls) ? ls : 0);
    const lh = parseFloat(cs.lineHeight);
    const fs = parseInt(cs.fontSize) || 16;
    setLineHeight(Number.isFinite(lh) && fs > 0 ? Math.round((lh / fs) * 100) / 100 : 1.5);
    setOpacity(Math.round(parseFloat(cs.opacity) * 100));
    setBorderRadius(parseInt(cs.borderRadius) || 0);
    setIsEditing(false);
    // Size
    const r = selectedDom.rect;
    setElWidth(Math.round(r.width));
    setElHeight(Math.round(r.height));
    setAspect(r.height > 0 ? r.width / r.height : 1);
    // Gradient detection from inline style
    const bgImg = (el.style.backgroundImage || "").trim();
    setGradientEnabled(bgImg.startsWith("linear-gradient"));
  }, [selectedDom]);

  const collectInlineStyles = useCallback((el: HTMLElement) => {
    const styles = PERSISTED_STYLE_PROPS.reduce<Record<string, string>>((acc, prop) => {
      const value = el.style.getPropertyValue(prop);
      if (value) acc[prop] = value;
      return acc;
    }, {});
    // Anchor hints so the applier can find this element even when the
    // DOM tree shifts (logged out vs admin, lazy content, re-renders).
    if (el.id) styles.__id = el.id;
    if (el.tagName.toLowerCase() === "img") {
      const img = el as HTMLImageElement;
      const src = img.getAttribute("src");
      if (src) styles.__src = src;
      if (img.alt) styles.__alt = img.alt;
    }
    return styles;
  }, []);

  const rememberDomOverride = useCallback((includeText = false) => {
    if (!selectedDom?.element) return;
    const styles = collectInlineStyles(selectedDom.element);
    if (includeText) {
      styles.__textContent = selectedDom.element.textContent?.trim() || "";
    }
    upsertPendingDomOverride({
      selector: selectedDom.selector,
      page: window.location.pathname,
      tagName: selectedDom.tagName,
      textContent: selectedDom.element.textContent?.trim().substring(0, 500) || selectedDom.text,
      styles,
    });
  }, [collectInlineStyles, selectedDom, upsertPendingDomOverride]);

  const applyStyle = useCallback((prop: string, value: string) => {
    if (!selectedDom?.element) return;
    selectedDom.element.style.setProperty(toKebabCase(prop), value, "important");
    upsertPendingDomOverride({
      selector: selectedDom.selector,
      page: window.location.pathname,
      tagName: selectedDom.tagName,
      textContent: selectedDom.element.textContent?.trim().substring(0, 500) || selectedDom.text,
      styles: collectInlineStyles(selectedDom.element),
    });
    setHasUnsavedChanges(true);
  }, [collectInlineStyles, selectedDom, setHasUnsavedChanges, upsertPendingDomOverride]);

  const handleTextColorChange = (color: string) => {
    setTextColor(color);
    applyStyle("color", color);
  };
  const handleBgColorChange = (color: string) => {
    setBgColor(color);
    applyStyle("backgroundColor", color);
  };
  const handleFontSizeChange = (size: number) => {
    setFontSize(size);
    applyStyle("fontSize", `${size}px`);
  };
  const handleFontFamilyChange = (family: string) => {
    setFontFamily(family);
    ensureFontLoaded(family);
    applyStyle("fontFamily", family ? `'${family}', sans-serif` : "");
  };
  const handleFontWeightChange = (weight: string) => {
    setFontWeight(weight);
    applyStyle("fontWeight", weight);
  };
  const toggleItalic = () => {
    const next = !italic;
    setItalic(next);
    applyStyle("fontStyle", next ? "italic" : "normal");
  };
  const updateDecoration = (nextUnderline: boolean, nextStrike: boolean) => {
    const parts: string[] = [];
    if (nextUnderline) parts.push("underline");
    if (nextStrike) parts.push("line-through");
    applyStyle("textDecorationLine", parts.length ? parts.join(" ") : "none");
  };
  const toggleUnderline = () => {
    const next = !underline;
    setUnderline(next);
    updateDecoration(next, strike);
  };
  const toggleStrike = () => {
    const next = !strike;
    setStrike(next);
    updateDecoration(underline, next);
  };
  const handleTextAlignChange = (align: string) => {
    setTextAlign(align);
    applyStyle("textAlign", align);
  };
  const handleTextTransformChange = (val: string) => {
    setTextTransform(val);
    applyStyle("textTransform", val);
  };
  const handleLetterSpacingChange = (val: number) => {
    setLetterSpacing(val);
    applyStyle("letterSpacing", `${val}px`);
  };
  const handleLineHeightChange = (val: number) => {
    setLineHeight(val);
    applyStyle("lineHeight", String(val));
  };
  const handleOpacityChange = (val: number) => {
    setOpacity(val);
    applyStyle("opacity", String(val / 100));
  };
  const handleBorderRadiusChange = (val: number) => {
    setBorderRadius(val);
    applyStyle("borderRadius", `${val}px`);
  };

  const applyGradient = (from: string, to: string, angle: number) => {
    applyStyle("backgroundImage", `linear-gradient(${angle}deg, ${from}, ${to})`);
  };
  const handleGradientToggle = (enabled: boolean) => {
    setGradientEnabled(enabled);
    if (enabled) applyGradient(gradFrom, gradTo, gradAngle);
    else applyStyle("backgroundImage", "none");
  };
  const handleGradFromChange = (c: string) => { setGradFrom(c); if (gradientEnabled) applyGradient(c, gradTo, gradAngle); };
  const handleGradToChange = (c: string) => { setGradTo(c); if (gradientEnabled) applyGradient(gradFrom, c, gradAngle); };
  const handleGradAngleChange = (a: number) => { setGradAngle(a); if (gradientEnabled) applyGradient(gradFrom, gradTo, a); };

  const enforceStretch = () => {
    if (!selectedDom?.element) return;
    const el = selectedDom.element;
    el.style.setProperty("max-width", "none", "important");
    el.style.setProperty("max-height", "none", "important");
    el.style.setProperty("min-width", "0", "important");
    el.style.setProperty("min-height", "0", "important");
    if (el.tagName.toLowerCase() === "img") {
      el.style.setProperty("object-fit", lockRatio ? "contain" : "fill", "important");
    }
  };

  const handleWidthChange = (w: number) => {
    setElWidth(w);
    enforceStretch();
    if (selectedDom?.element) selectedDom.element.style.setProperty("width", `${w}px`, "important");
    if (lockRatio && aspect > 0) {
      const h = Math.round(w / aspect);
      setElHeight(h);
      if (selectedDom?.element) selectedDom.element.style.setProperty("height", `${h}px`, "important");
    }
    rememberDomOverride();
    setHasUnsavedChanges(true);
  };
  const handleHeightChange = (h: number) => {
    setElHeight(h);
    enforceStretch();
    if (selectedDom?.element) selectedDom.element.style.setProperty("height", `${h}px`, "important");
    if (lockRatio && aspect > 0) {
      const w = Math.round(h * aspect);
      setElWidth(w);
      if (selectedDom?.element) selectedDom.element.style.setProperty("width", `${w}px`, "important");
    }
    rememberDomOverride();
    setHasUnsavedChanges(true);
  };
  const resetSize = () => {
    if (!selectedDom?.element) return;
    const el = selectedDom.element;
    ["width","height","max-width","max-height","min-width","min-height","object-fit"].forEach((p) => el.style.removeProperty(p));
    const r = el.getBoundingClientRect();
    setElWidth(Math.round(r.width));
    setElHeight(Math.round(r.height));
    rememberDomOverride();
    setHasUnsavedChanges(true);
  };

  const toggleInlineEdit = () => {
    if (!selectedDom?.element) return;
    const el = selectedDom.element;
    const tag = el.tagName.toLowerCase();
    if (!["h1","h2","h3","h4","h5","h6","p","span","a","li","label","button"].includes(tag)) {
      toast({ title: "Cet élément ne supporte pas l'édition de texte" });
      return;
    }
    if (isEditing) {
      el.contentEditable = "false";
      el.style.outline = "";
      setIsEditing(false);
      rememberDomOverride(true);
      setHasUnsavedChanges(true);
    } else {
      el.contentEditable = "true";
      el.style.outline = "2px dashed hsl(var(--primary))";
      el.focus();
      setIsEditing(true);
    }
  };

  if (!isDesignMode || !selectedDom) return null;

  const isTextElement = ["h1","h2","h3","h4","h5","h6","p","span","a","li","label","button"].includes(selectedDom.tagName);
  const isButton = selectedDom.tagName === "button";

  return (
    <div
      data-design-panel
      data-editor-ui
      className="fixed right-4 top-20 z-[9998] w-80 bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between p-3 bg-muted/50 border-b border-border">
        <span className="text-sm font-semibold flex items-center gap-1.5">
          <span className="bg-primary/10 text-primary text-[10px] font-mono px-1.5 py-0.5 rounded">
            {selectedDom.tagName}
          </span>
          {selectedDom.text.substring(0, 20)}{selectedDom.text.length > 20 ? "…" : ""}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => {
            if (isEditing && selectedDom.element) {
              selectedDom.element.contentEditable = "false";
              selectedDom.element.style.outline = "";
            }
            setSelectedDom(null);
          }}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="p-3 space-y-4 max-h-[70vh] overflow-y-auto text-sm">
        {isTextElement && (
          <Button size="sm" variant={isEditing ? "default" : "outline"} onClick={toggleInlineEdit} className="w-full">
            <Type className="w-4 h-4 mr-1" />
            {isEditing ? "Terminer l'édition" : "Modifier le texte"}
          </Button>
        )}

        {/* TYPOGRAPHY */}
        {isTextElement && (
          <div className="space-y-3 pb-3 border-b border-border">
            <Label className="text-xs font-semibold uppercase text-muted-foreground">Typographie</Label>

            <div>
              <Label className="text-xs mb-1">Police</Label>
              <Select value={fontFamily || "__default"} onValueChange={(v) => handleFontFamilyChange(v === "__default" ? "" : v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Police par défaut" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__default">Police par défaut</SelectItem>
                  {FONT_OPTIONS.map((f) => (
                    <SelectItem key={f.family} value={f.family} style={{ fontFamily: `'${f.family}', sans-serif` }}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs mb-1">Graisse</Label>
              <Select value={fontWeight} onValueChange={handleFontWeightChange}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FONT_WEIGHT_OPTIONS.map((w) => (
                    <SelectItem key={w.value} value={w.value}>{w.label} ({w.value})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs mb-1">Taille : {fontSize}px</Label>
              <Slider value={[fontSize]} min={8} max={120} step={1} onValueChange={([v]) => handleFontSizeChange(v)} />
            </div>

            <div className="flex gap-1">
              <Button size="sm" variant={fontWeight === "700" || fontWeight === "800" ? "default" : "outline"} className="h-8 w-8 p-0" onClick={() => handleFontWeightChange(fontWeight === "700" ? "400" : "700")} title="Gras">
                <Bold className="w-4 h-4" />
              </Button>
              <Button size="sm" variant={italic ? "default" : "outline"} className="h-8 w-8 p-0" onClick={toggleItalic} title="Italique">
                <Italic className="w-4 h-4" />
              </Button>
              <Button size="sm" variant={underline ? "default" : "outline"} className="h-8 w-8 p-0" onClick={toggleUnderline} title="Souligné">
                <Underline className="w-4 h-4" />
              </Button>
              <Button size="sm" variant={strike ? "default" : "outline"} className="h-8 w-8 p-0" onClick={toggleStrike} title="Barré">
                <Strikethrough className="w-4 h-4" />
              </Button>
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1 mb-1"><Palette className="w-3 h-3" /> Couleur</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={textColor} onChange={(e) => handleTextColorChange(e.target.value)} className="w-8 h-8 rounded border cursor-pointer" />
                <Input value={textColor} onChange={(e) => handleTextColorChange(e.target.value)} className="h-8 text-xs font-mono flex-1" />
              </div>
            </div>

            <div>
              <Label className="text-xs mb-1">Alignement</Label>
              <div className="flex gap-1">
                {[{value:"left",icon:AlignLeft},{value:"center",icon:AlignCenter},{value:"right",icon:AlignRight}].map(({value,icon:Icon}) => (
                  <Button key={value} size="sm" variant={textAlign === value ? "default" : "outline"} className="h-8 w-8 p-0" onClick={() => handleTextAlignChange(value)}>
                    <Icon className="w-4 h-4" />
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs mb-1">Casse</Label>
              <div className="flex gap-1">
                {[
                  {value:"none",icon:CaseSensitive,title:"Aucune"},
                  {value:"uppercase",icon:CaseUpper,title:"MAJUSCULES"},
                  {value:"lowercase",icon:CaseLower,title:"minuscules"},
                  {value:"capitalize",icon:Type,title:"Initiales"},
                ].map(({value,icon:Icon,title}) => (
                  <Button key={value} size="sm" variant={textTransform === value ? "default" : "outline"} className="h-8 w-8 p-0" onClick={() => handleTextTransformChange(value)} title={title}>
                    <Icon className="w-4 h-4" />
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs mb-1">Espacement lettres : {letterSpacing.toFixed(1)}px</Label>
              <Slider value={[letterSpacing]} min={-5} max={20} step={0.5} onValueChange={([v]) => handleLetterSpacingChange(v)} />
            </div>

            <div>
              <Label className="text-xs mb-1">Hauteur de ligne : {lineHeight.toFixed(2)}</Label>
              <Slider value={[lineHeight * 100]} min={80} max={300} step={5} onValueChange={([v]) => handleLineHeightChange(v / 100)} />
            </div>
          </div>
        )}

        {/* Background color */}
        <div>
          <Label className="text-xs flex items-center gap-1 mb-1"><Palette className="w-3 h-3" /> Couleur de fond</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={bgColor === "transparent" ? "#ffffff" : bgColor} onChange={(e) => handleBgColorChange(e.target.value)} className="w-8 h-8 rounded border cursor-pointer" />
            <Input value={bgColor} onChange={(e) => handleBgColorChange(e.target.value)} className="h-8 text-xs font-mono flex-1" />
          </div>
        </div>

        <div>
          <Label className="text-xs mb-1">Opacité : {opacity}%</Label>
          <Slider value={[opacity]} min={5} max={100} step={5} onValueChange={([v]) => handleOpacityChange(v)} />
        </div>

        {(isButton || selectedDom.tagName === "section" || selectedDom.tagName === "div") && (
          <div>
            <Label className="text-xs mb-1">Arrondi : {borderRadius}px</Label>
            <Slider value={[borderRadius]} min={0} max={50} step={1} onValueChange={([v]) => handleBorderRadiusChange(v)} />
          </div>
        )}

        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs flex items-center gap-1"><Sparkles className="w-3 h-3" /> Dégradé de fond</Label>
            <input type="checkbox" checked={gradientEnabled} onChange={(e) => handleGradientToggle(e.target.checked)} className="h-4 w-4 cursor-pointer" />
          </div>
          {gradientEnabled && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input type="color" value={gradFrom} onChange={(e) => handleGradFromChange(e.target.value)} className="w-8 h-8 rounded border cursor-pointer" />
                <span className="text-[10px] text-muted-foreground">vers</span>
                <input type="color" value={gradTo} onChange={(e) => handleGradToChange(e.target.value)} className="w-8 h-8 rounded border cursor-pointer" />
              </div>
              <Label className="text-xs">Angle : {gradAngle}°</Label>
              <Slider value={[gradAngle]} min={0} max={360} step={5} onValueChange={([v]) => handleGradAngleChange(v)} />
              <div className="h-6 rounded border" style={{ background: `linear-gradient(${gradAngle}deg, ${gradFrom}, ${gradTo})` }} />
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs flex items-center gap-1"><Maximize2 className="w-3 h-3" /> Redimensionner librement</Label>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={resetSize} title="Réinitialiser">
              <RotateCcw className="w-3 h-3" />
            </Button>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={lockRatio}
              onChange={(e) => {
                setLockRatio(e.target.checked);
                if (selectedDom?.element?.tagName.toLowerCase() === "img") {
                  selectedDom.element.style.setProperty("object-fit", e.target.checked ? "contain" : "fill", "important");
                  rememberDomOverride();
                }
              }}
              className="h-3.5 w-3.5 cursor-pointer"
              id="lock-ratio"
            />
            <Label htmlFor="lock-ratio" className="text-[11px] cursor-pointer">Conserver les proportions</Label>
          </div>
          <Label className="text-xs mb-1">Largeur : {elWidth}px</Label>
          <Slider value={[elWidth]} min={20} max={1600} step={1} onValueChange={([v]) => handleWidthChange(v)} />
          <Label className="text-xs mb-1 mt-2">Hauteur : {elHeight}px</Label>
          <Slider value={[elHeight]} min={20} max={1200} step={1} onValueChange={([v]) => handleHeightChange(v)} />
        </div>

        <div className="pt-2 border-t border-border">
          <p className="text-[10px] text-muted-foreground font-mono break-all">{selectedDom.selector}</p>
        </div>
      </div>
    </div>
  );
};

function rgbToHex(rgb: string): string {
  if (rgb.startsWith("#")) return rgb;
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) return "#000000";
  const hex = match.slice(0, 3).map(n => parseInt(n).toString(16).padStart(2, "0")).join("");
  return `#${hex}`;
}

export default DesignDomPanel;
